import { DEFAULT_COMMISSION_RATE } from './types';

export type PartnerApplicationInput = {
  companyName: string;
  cnpj?: string | null;
};

export type PartnerApplicationPayload = {
  company_name: string;
  cnpj: string | null;
  status: 'pending';
  commission_rate: typeof DEFAULT_COMMISSION_RATE;
};

export type PartnerApplicationValidation =
  | { ok: true; payload: PartnerApplicationPayload }
  | { ok: false; field: 'companyName' | 'cnpj'; message: string };

/** Digits only from CNPJ input. */
export function digitsOnlyCnpj(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

/** Format 14 digits as 00.000.000/0000-00 for display. */
export function formatCnpjDisplay(value: string | null | undefined): string {
  const digits = digitsOnlyCnpj(value).slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/**
 * Validates partner apply form. CNPJ optional; when present must be 14 digits.
 * Status/rate are fixed — never accept privileged fields from the client UI.
 */
export function validatePartnerApplication(
  input: PartnerApplicationInput,
): PartnerApplicationValidation {
  const companyName = input.companyName.trim();
  if (companyName.length < 2) {
    return {
      ok: false,
      field: 'companyName',
      message: 'Informe o nome da agência (mínimo 2 caracteres).',
    };
  }
  if (companyName.length > 120) {
    return {
      ok: false,
      field: 'companyName',
      message: 'Nome da agência deve ter no máximo 120 caracteres.',
    };
  }

  const cnpjDigits = digitsOnlyCnpj(input.cnpj);
  if (cnpjDigits.length > 0 && cnpjDigits.length !== 14) {
    return {
      ok: false,
      field: 'cnpj',
      message: 'CNPJ deve ter 14 dígitos.',
    };
  }

  return {
    ok: true,
    payload: {
      company_name: companyName,
      cnpj: cnpjDigits.length === 14 ? cnpjDigits : null,
      status: 'pending',
      commission_rate: DEFAULT_COMMISSION_RATE,
    },
  };
}
